import React, { useContext, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import Auth from '@cpg-its/ads.auth.auth';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import SessionTimeout from '@cpg-its/ads.auth.session-timeout';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { LocalStorageKeys } from '../constants/local-storage';
import { AuthenticatedState, AuthToken } from '../models/authenticated-model';
import { dispatchSetApplicationList, setAccessToken, setBenefitFacts, setClientGuid, setRoleObject, setRoles } from '../ducks/authenticated-slice';
import { TcsmStates } from '../../root/tcsm-model';
import { ProfileKey } from '../../pages/arc/constants/constant-strings';
import { getApplications, getRoles } from '../ducks/wam-api';
import { AuthSetting } from '../constants/auth-settings';
import { getPersonBenefitFacts } from '../ducks/mlps-api';
import { setCookie } from '../helpers/auth-helpers';
import { YesNoIndicators } from '../models/api-model';

interface AuthContentProps {
	redirectUri: string;
}

const onlyUnique = (value, index, self) => {
	return self.indexOf(value) === index;
};

const parseRoleNames = (data: any) => {
	if (!data || !data?.role_assignments) {
		return [];
	}
	const keys = Object.keys(data?.role_assignments);
	return Array.prototype.concat.apply([], keys?.map(key => data?.role_assignments[key].map(a => a?.role_name)));
};

const cookieTime = 0.04;
function AuthContent(props: React.PropsWithChildren<AuthContentProps>) {

	const { children, redirectUri } = props;
	const { error, isLoading, isAuthenticated, loginWithRedirect, getAccessToken, accessToken, decodedToken } = useContext(AuthContext) as any;
	const authenticatedState = useSelector<TcsmStates, AuthenticatedState>(state => state?.authenticatedState);

	const dispatch = useDispatch();

	const parsedGuid = (() => {
		if (decodedToken && decodedToken[ProfileKey]) {
			return (JSON.parse(decodedToken[ProfileKey]) as AuthToken)?.person?.guid;
		}
		return decodedToken;
	})();

	// store token in redux state whenever it changes
	useEffect(() => {
		if (accessToken) {
			dispatch(setAccessToken(accessToken));
		}

		if (!authenticatedState?.clientGuid && parsedGuid) {
			dispatch(setClientGuid(parsedGuid));
			dispatch(getPersonBenefitFacts(parsedGuid, (response) => {
				dispatch(setBenefitFacts(response?.data));
				Object.keys(response?.data)?.forEach(vg => {
					document.cookie = `${vg}=${response?.data[vg]}; path=/`;
				});
			}));

			dispatch(getRoles(
				parsedGuid,
				(res) => {
					const roles = parseRoleNames(res?.data)?.filter(onlyUnique);
					sessionStorage?.setItem('rolesMatrix', JSON.stringify(roles));
					if (roles?.includes('CIC_User')) 
						setCookie('CICUSER', YesNoIndicators.Yes, cookieTime);
					else
						setCookie('CICUSER', YesNoIndicators.No, cookieTime);
					dispatch(setRoles(roles));
					dispatch(setRoleObject(res?.data?.role_assignments || {}));
				},
			));

			dispatch(getApplications((res) => dispatchSetApplicationList(res, dispatch)));

			if (!sessionStorage?.getItem('clientGuid') !== parsedGuid) {
				sessionStorage?.setItem('clientGuid', parsedGuid);

			}
		}
	}, [accessToken, decodedToken, authenticatedState?.clientGuid]);

	if (error) {
		return (
			<div>{error.message}</div>
		);
	}

	// must check isLoading before check isAuthenticated
	if (isLoading) {
		return (<LoadingCircle />);
	}

	// store original requested route in local storage before redirecting to login
	if (!isAuthenticated) {
		localStorage.setItem(LocalStorageKeys.AuthRedirect, window.location.pathname + window.location.search);
		loginWithRedirect();
	}

	if (!isAuthenticated || !authenticatedState.accessToken) {
		return <LoadingCircle />;
	}

	// once authenticated, route to the original requested route, then remove it from local storage
	const authRedirect = localStorage.getItem(LocalStorageKeys.AuthRedirect);
	if (authRedirect) {
		localStorage.removeItem(LocalStorageKeys.AuthRedirect);
		return <Redirect to={authRedirect} />;
	}

	const encodedReturnTo = encodeURIComponent(`${AuthSetting.REACT_APP_AUTH0_LOGOUT_URL}?returnTo=${redirectUri}&client_id=${AuthSetting.REACT_APP_CLIENT_ID}`);
	const logoutUrl = `${AuthSetting.REACT_APP_CPG_LOGOUT_URL}?returnTo=${encodedReturnTo}`;

	return (
		<>
			<SessionTimeout
				onActivity={() => getAccessToken()}
				logoutUrl={logoutUrl}
				timeout={AuthSetting.REACT_APP_SESSION_TIMEOUT}
				countdown={AuthSetting.REACT_APP_SESSION_COUNTDOWN}
				throttle={AuthSetting.REACT_APP_SESSION_THROTTLE}
			/>
			{children}
		</>
	);
}

interface CustomAuthProps {
	redirectUri: string;
}
export function CustomAuth(props: React.PropsWithChildren<CustomAuthProps>) {
	const { redirectUri, children } = props;

	return (
		<Auth
			audience={AuthSetting.REACT_APP_AUDIENCE}
			clientId={AuthSetting.REACT_APP_CLIENT_ID}
			connection={AuthSetting.REACT_APP_CONNECTION}
			domain={AuthSetting.REACT_APP_DOMAIN}
			redirectUri={redirectUri}>
			<AuthContent redirectUri={redirectUri}>
				{children}
			</AuthContent>
		</Auth>
	);
}